import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";

/**
 * columns: [{ key, label, render?(row), align?, sortable?, sortValue?(row) }]
 * data: array
 * searchKeys: array of keys to search
 */
export default function DataTable({
  columns, data = [], searchKeys = [], pageSize = 10, testid = "table",
  toolbar, emptyText = "Tidak ada data", rowKey = "id",
  selectable = false, selectedIds = [], onToggleRow, onToggleAll, onVisibleChange,
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let rows = data;
    if (q && searchKeys.length) {
      const s = q.toLowerCase();
      rows = rows.filter((r) =>
        searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(s))
      );
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      rows = [...rows].sort((a, b) => {
        const av = col?.sortValue ? col.sortValue(a) : a[sort.key];
        const bv = col?.sortValue ? col.sortValue(b) : b[sort.key];
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
        return String(av).localeCompare(String(bv), "id") * sort.dir;
      });
    }
    return rows;
  }, [data, q, sort, columns, searchKeys]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const visibleKey = filtered.map((r) => r[rowKey]).join(",");
  React.useEffect(() => {
    if (onVisibleChange) onVisibleChange(filtered.map((r) => r[rowKey]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey]);
  const allSelected = selectable && filtered.length > 0 && filtered.every((r) => selectedIds.includes(r[rowKey]));

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {searchKeys.length > 0 && (
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={`${testid}-search`}
              placeholder="Cari..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
        )}
        {toolbar && <div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
      </div>

      <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100/80">
              {selectable && (
                <th className="w-10 px-3 py-2.5 text-left">
                  <input type="checkbox" className="h-4 w-4 cursor-pointer accent-blue-800" data-testid={`${testid}-select-all`}
                    checked={allSelected} onChange={(e) => onToggleAll && onToggleAll(filtered.map((r) => r[rowKey]), e.target.checked)} />
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => c.sortable && toggleSort(c.key)}
                  className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap ${
                    c.align === "right" ? "text-right" : "text-left"
                  } ${c.sortable ? "cursor-pointer select-none hover:text-slate-800" : ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && sort.key === c.key &&
                      (sort.dir === 1 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-3 py-10 text-center text-slate-400">
                  {emptyText}
                </td>
              </tr>
            ) : (
              paged.map((row, i) => (
                <tr
                  key={row[rowKey] || i}
                  data-testid={`${testid}-row-${i}`}
                  className="border-b border-slate-100 transition-colors hover:bg-slate-50"
                >
                  {selectable && (
                    <td className="w-10 px-3 py-2.5">
                      <input type="checkbox" className="h-4 w-4 cursor-pointer accent-blue-800" data-testid={`${testid}-select-${i}`}
                        checked={selectedIds.includes(row[rowKey])} onChange={() => onToggleRow && onToggleRow(row[rowKey])} />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2.5 ${c.align === "right" ? "text-right font-mono tabular" : "text-left"} ${c.className || ""}`}
                    >
                      {c.render ? c.render(row) : row[c.key] ?? "-"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{filtered.length} data</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid={`${testid}-prev`}
              disabled={curPage <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>Hal {curPage} / {totalPages}</span>
            <Button variant="outline" size="sm" data-testid={`${testid}-next`}
              disabled={curPage >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function toCSV(filename, columns, rows) {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows
    .map((r) => columns.map((c) => `"${String(c.value(r) ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["\uFEFF" + header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
